import { createAsyncThunk, createSlice, Dispatch, PayloadAction } from "@reduxjs/toolkit";
import { OnboardingStep, TUpdateProfile, TUser } from "../../models/user.model";
import authService from "@services/auth.service";
import { TRequestStatus, TStep1Data } from "@models/msc.model";
import { getAuthToken, removeAuthToken, setAuthProvider, setAuthToken, setRefreshToken } from "@utils/axios-instance";
import meService from "@/services/me.service";
import metaService from "@/services/meta.service";
import googleAdsService, { GoogleAdsAccount } from "@/services/google.ads.service";
import onboardingService from "@/services/onboarding.service";
import { RootState } from "..";
import { TMetaAdAccount } from "@/models/meta.model";
import { TMyCallCenterPreferences } from "@/models/call.center.model";

const TOTAL_ONBOARDING_STEPS = 5;

const initialUser: TUser = {
  id: 0,
  status: 0,
  companyUniqueId: "",
  companyUniqueName: "",
  email: "",
  firstName: "",
  lastName: "",
  timezone: "",
  availability: [],
  avatar: null,
  roles: [],
  teams: [],
  created: "",
  updated: "",
  defaultCompanyDomain: "",
};

type AuthState = {
  user: TUser;
  initialized: boolean;
  isLoggedIn: boolean;
  status: TRequestStatus;
  accessToken: string | null;
};

//-----THUNK FUNCTIONS-----
export const login = createAsyncThunk("auth/login", authService.login);
export const getProfile = createAsyncThunk("auth/getMe", meService.getMe);

const initialState: AuthState = {
  user: initialUser,
  initialized: false,
  isLoggedIn: getAuthToken() !== null,
  status: "IDLE",
  accessToken: null,
};

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    initializeApp: (state) => {
      if (getAuthToken() === null) {
        state.initialized = true;
        return;
      }
      setAuthToken();
    },
    logout: (state) => {
      removeAuthToken();
      state.user = initialUser;
      state.isLoggedIn = false;
      state.accessToken = null;
    },
    hubspotAccountDisconnected: (state) => {
      state.user.hubspotConnected = false;
    },
    userLoggedIn: (state) => {
      state.isLoggedIn = true;
    },
    userAvailabilitySaved: (state, action) => {
      if (!state.user) return;
      const updatedUser = { ...state.user };
      updatedUser.availability = action.payload.availability;
      updatedUser.timezone = action.payload.timezone;
      state.user = updatedUser;
    },
    profileUpdated: (state, action: PayloadAction<Omit<TUpdateProfile, "email">>) => {
      if (!state.user) return;
      state.user = { ...state.user, ...action.payload };
    },
    myCallCenterPreferencesUpdated: (state, action: PayloadAction<Partial<TMyCallCenterPreferences>>) => {
      if (!state.user) return;
      state.user = { ...state.user, ...action.payload };
    },
    onboardingUpdated: (state, action) => {
      if (!state.user) return;
      state.user.onboarding = { ...state.user.onboarding, ...action.payload };
    },
    localMeUpdated: (state, action) => {
      if (!state.user) return;
      state.user = { ...state.user, ...action.payload };
    },
    stepSubmitted: (state) => {
      if (state.user?.onboarding) {
        const newStep = state.user.onboarding.currentStep + 1;
        state.user.onboarding.currentStep = newStep;
        if (newStep >= TOTAL_ONBOARDING_STEPS) {
          delete state.user.onboarding;
        }
      }
    },
    stepSkipped: (state) => {
      if (state.user?.onboarding) {
        const newStep = state.user.onboarding.currentStep + 1;
        state.user.onboarding.currentStep = newStep;
        if (newStep >= TOTAL_ONBOARDING_STEPS) {
          delete state.user.onboarding;
        }
      }
    },
    metaAccountDisconnected: (state) => {
      state.user.metaConnected = false;
      state.user.metaClientId = undefined;
      state.user.metaClientName = undefined;
    },
    metaAccountUpdated: (state, action: PayloadAction<{ metaClientId: string; metaClientName: string }>) => {
      state.user.metaClientId = action.payload.metaClientId;
      state.user.metaClientName = action.payload.metaClientName;
    },
    googleAdsAccountDisconnected: (state) => {
      state.user.googleAdsConnected = false;
      state.user.googleAdClientId = undefined;
      state.user.googleAdClientName = undefined;
    },
    googleAdsAccountUpdated: (state, action: PayloadAction<GoogleAdsAccount>) => {
      state.user.googleAdClientId = action.payload.id;
      state.user.googleAdClientName = action.payload.name;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(login.pending, (state) => {
      state.status = "PENDING";
    });
    builder.addCase(login.rejected, (state) => {
      state.status = "REJECTED";
    });
    builder.addCase(login.fulfilled, (state, action) => {
      // inject token to axios instance
      const { payload } = action;
      const { AccessToken, RefreshToken, AuthProvider } = payload;
      state.accessToken = AccessToken;
      setAuthToken(AccessToken);
      setRefreshToken(RefreshToken);
      setAuthProvider(AuthProvider);

      state.status = "IDLE";
      state.isLoggedIn = true;
    });

    /*--------------------------------------------------*/
    builder.addCase(getProfile.rejected, (state) => {
      removeAuthToken();
      state.isLoggedIn = false;
      state.initialized = true;
    });

    builder.addCase(getProfile.fulfilled, (state, action) => {
      const { payload } = action;
      state.user = payload;
      state.initialized = true;
      state.isLoggedIn = true;
    });
  },
});

// ----------------------------
// AUTH ACTIONS
const { initializeApp, logout, userAvailabilitySaved, userLoggedIn, hubspotAccountDisconnected } = authSlice.actions;
export { initializeApp, logout, userAvailabilitySaved, userLoggedIn, hubspotAccountDisconnected };
// ----------------------------

// ----------------------------

export const updateProfile = (profile: Omit<TUpdateProfile, "email">) => async (dispatch: Dispatch) => {
  const response = await meService.updateProfile(profile);
  dispatch(authSlice.actions.profileUpdated(profile));
  return response;
};

export const updateMyCallCenterPreferences = (preferences: Partial<TMyCallCenterPreferences>) => async (dispatch: Dispatch) => {
  const response = await meService.updateMyCallCenterPreferences(preferences);
  dispatch(authSlice.actions.myCallCenterPreferencesUpdated({ ...preferences }));
  return response;
};



export const updateOnboarding =
  (data: { currentStep: OnboardingStep }) => async (dispatch: Dispatch, getState: () => RootState) => {
    const currentOnboarding = getState().auth.user?.onboarding;
    //optimistic scenario
    if (currentOnboarding) {
      const updatedOnboarding = { ...currentOnboarding, ...data };
      dispatch(authSlice.actions.onboardingUpdated(updatedOnboarding));
    }

    try {
      const response = await onboardingService.updateOnboarding(data);
      dispatch(authSlice.actions.onboardingUpdated(response));
      return response;
    } catch (err) {
      //roll back
      dispatch(authSlice.actions.onboardingUpdated(currentOnboarding));
      throw err;
    }
  };

export const updateLocalMe = (data: Partial<TUser>) => async (dispatch: Dispatch) => {
  dispatch(authSlice.actions.localMeUpdated(data));
};

// CALL CENTER ONBOARDING
export const skipStep = (step: number) => async (dispatch: Dispatch) => {
  await onboardingService.skipStep(step);
  dispatch(authSlice.actions.stepSkipped());
};

export const submitStep1 = (data: TStep1Data) => async (dispatch: Dispatch) => {
  await onboardingService.submitStep1(data);
  dispatch(authSlice.actions.stepSubmitted());
};

export const submitStep2 = () => async (dispatch: Dispatch) => {
  dispatch(authSlice.actions.stepSubmitted());
};

export const submitStep3 = (file: File) => async (dispatch: Dispatch) => {
  await onboardingService.submitStep3(file);
  dispatch(authSlice.actions.stepSubmitted());
};

export const submitStep4 = (file: File) => async (dispatch: Dispatch) => {
  await onboardingService.submitStep4(file);
  dispatch(authSlice.actions.stepSubmitted());
};

export const submitStep5 = (file: File) => async (dispatch: Dispatch) => {
  await onboardingService.submitStep5(file);
  dispatch(authSlice.actions.stepSubmitted());
};

// INTEGRATIONS
export const disconnectMetaAccount = () => async (dispatch: Dispatch) => {
  await metaService.disconnectMetaAccount();
  dispatch(authSlice.actions.metaAccountDisconnected());
};

export const updateMetaAccount = (account: TMetaAdAccount) => async (dispatch: Dispatch) => {
  await metaService.updateCurrentAdAccount(account);
  dispatch(authSlice.actions.metaAccountUpdated({ metaClientId: account.id, metaClientName: account.name }));
};

export const disconnectGoogleAdsAccount = () => async (dispatch: Dispatch) => {
  await googleAdsService.disconnectGoogleAdsAccount();
  dispatch(authSlice.actions.googleAdsAccountDisconnected());
};

export const updateGoogleAdsAccount = (account: GoogleAdsAccount) => async (dispatch: Dispatch) => {
  await googleAdsService.updateCurrentAdAccount(account);
  dispatch(authSlice.actions.googleAdsAccountUpdated(account));
};
export default authSlice.reducer;
